import React, { useState, useEffect, useMemo } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/Card';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { EmptyState } from '../components/ui/EmptyState';
import { formatBRL } from '../utils/FormatDecimal';
import {
  CashflowForecastEntry,
  CashflowForecastResponse,
  CashflowForecastWeek,
  getCashflowForecast,
} from '../services/apiService';

const HORIZON_OPTIONS = [4, 8, 13, 26];
const LOOKBACK_OPTIONS = [1, 2, 3, 6, 12];

const SOURCE_LABELS: Record<CashflowForecastEntry['source'], string> = {
  realized: 'Realizado',
  loan: 'Empréstimo',
  historical: 'Histórico',
  provisioned: 'Provisionado',
};

const SOURCE_COLORS: Record<CashflowForecastEntry['source'], string> = {
  realized: 'bg-gray-100 text-gray-700',
  loan: 'bg-purple-100 text-purple-700',
  historical: 'bg-blue-100 text-blue-700',
  provisioned: 'bg-amber-100 text-amber-700',
};

const GROUP_LABELS: Record<string, string> = {
  operacional: 'Operacional',
  investimento: 'Investimento',
  financiamento: 'Financiamento',
  socios: 'Sócios',
};

function formatWeekLabel(week: CashflowForecastWeek): string {
  const start = week.week_start.slice(5);  // MM-DD
  const end = week.week_end.slice(5);
  return `${start} → ${end}`;
}

function EntryRow({ entry }: { entry: CashflowForecastEntry }) {
  const badge = SOURCE_COLORS[entry.source];
  const sign = entry.source === 'realized' ? '' : '~';
  return (
    <tr className="border-b border-gray-100 hover:bg-blue-50/50">
      <td className="py-1.5 px-2 text-gray-500 font-mono text-xs">{entry.date.slice(5)}</td>
      <td className="py-1.5 px-2">
        <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase ${badge}`}>
          {SOURCE_LABELS[entry.source]}
        </span>
      </td>
      <td className="py-1.5 px-2 text-gray-700 text-xs truncate max-w-xs" title={entry.account}>
        {entry.account.split(':').slice(-2).join(':')}
      </td>
      <td className="py-1.5 px-2 text-gray-600 text-xs truncate max-w-xs" title={entry.description}>
        {entry.description}
      </td>
      <td className="py-1.5 px-2 text-right font-mono text-xs">
        {sign}{formatBRL(entry.amount)}
      </td>
    </tr>
  );
}

function WeekRow({ week, index, expanded, onToggle }: {
  week: CashflowForecastWeek;
  index: number;
  expanded: boolean;
  onToggle: () => void;
}) {
  const hasEntries = week.inflows.length + week.outflows.length > 0;
  const negative = week.closing_balance < 0;

  return (
    <>
      <tr
        className={`border-b border-gray-200 cursor-pointer ${expanded ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
        onClick={onToggle}
      >
        <td className="py-3 px-3 w-8">
          {hasEntries ? (
            expanded ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />
          ) : (
            <span className="w-4 inline-block" />
          )}
        </td>
        <td className="py-3 px-3 text-xs text-gray-500 font-mono">#{index + 1}</td>
        <td className="py-3 px-3 text-sm">
          <div className="font-medium text-gray-800">{formatWeekLabel(week)}</div>
          <div className="text-xs text-gray-400">{week.week_start.slice(0, 4)}</div>
        </td>
        <td className="py-3 px-3 text-right font-mono text-sm text-gray-500">
          {formatBRL(week.opening_balance)}
        </td>
        <td className="py-3 px-3 text-right font-mono text-sm text-green-600">
          {week.total_inflows > 0 ? `+${formatBRL(week.total_inflows)}` : '—'}
        </td>
        <td className="py-3 px-3 text-right font-mono text-sm text-red-600">
          {week.total_outflows > 0 ? `−${formatBRL(week.total_outflows)}` : '—'}
        </td>
        <td className={`py-3 px-3 text-right font-mono text-sm font-medium ${week.net >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
          {week.net >= 0 ? '+' : ''}{formatBRL(week.net)}
        </td>
        <td className={`py-3 px-3 text-right font-mono text-sm font-semibold ${negative ? 'text-red-700' : 'text-gray-800'}`}>
          {formatBRL(week.closing_balance)}
        </td>
      </tr>
      {expanded && hasEntries && (
        <tr>
          <td colSpan={8} className="bg-blue-50/30 p-0">
            <div className="px-8 py-3">
              {week.inflows.length > 0 && (
                <div className="mb-3">
                  <div className="text-xs font-semibold text-green-700 uppercase tracking-wider mb-1">
                    Entradas ({week.inflows.length})
                  </div>
                  <table className="w-full">
                    <tbody>
                      {week.inflows.map((e, i) => <EntryRow key={`in-${i}`} entry={e} />)}
                    </tbody>
                  </table>
                </div>
              )}
              {week.outflows.length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-red-700 uppercase tracking-wider mb-1">
                    Saídas ({week.outflows.length})
                  </div>
                  <table className="w-full">
                    <tbody>
                      {week.outflows.map((e, i) => <EntryRow key={`out-${i}`} entry={e} />)}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function CashflowForecastPage() {
  const [weeks, setWeeks] = useState(13);
  const [lookbackMonths, setLookbackMonths] = useState(3);
  const [data, setData] = useState<CashflowForecastResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set([0]));

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getCashflowForecast({ weeks, lookback_months: lookbackMonths })
      .then((r) => { if (!cancelled) setData(r); })
      .catch((err: any) => {
        if (!cancelled) setError(err.response?.data?.detail || 'Erro ao carregar projeção');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [weeks, lookbackMonths]);

  const chartData = useMemo(() => {
    if (!data) return [];
    return [
      { label: 'Hoje', balance: data.opening_balance, net: 0 },
      ...data.weeks.map((w, i) => ({
        label: `S${i + 1}`,
        balance: w.closing_balance,
        net: w.net,
        inflows: w.total_inflows,
        outflows: -w.total_outflows,
        week_range: formatWeekLabel(w),
      })),
    ];
  }, [data]);

  if (loading && !data) return <LoadingSpinner message="Calculando projeção..." />;
  if (error) return <EmptyState title="Erro" description={error} />;
  if (!data) return <EmptyState title="Sem dados" description="" />;

  const minBalance = data.summary.min_balance.amount;
  const minBalanceNegative = minBalance != null && minBalance < 0;

  return (
    <div className="space-y-6">
      {/* Header + horizon selector */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs text-gray-500">
            Projeção rolling direta (AR + AP + compromissos) · hoje: {data.today}
          </p>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Histórico:</span>
            {LOOKBACK_OPTIONS.map((m) => (
              <button
                key={m}
                onClick={() => setLookbackMonths(m)}
                className={`px-2.5 py-1 text-xs rounded ${
                  lookbackMonths === m
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                title={`Usar ${m} meses de histórico para projeção`}
              >
                {m}m
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Horizonte:</span>
            {HORIZON_OPTIONS.map((w) => (
              <button
                key={w}
                onClick={() => setWeeks(w)}
                className={`px-2.5 py-1 text-xs rounded ${
                  weeks === w
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {w}s
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent>
            <p className="text-xs text-gray-500 uppercase tracking-wider">Saldo Inicial</p>
            <p className={`text-lg font-semibold font-mono mt-1 ${data.opening_balance >= 0 ? 'text-gray-800' : 'text-red-600'}`}>
              {formatBRL(data.opening_balance)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <p className="text-xs text-gray-500 uppercase tracking-wider">Net do Período</p>
            <p className={`text-lg font-semibold font-mono mt-1 ${data.summary.net >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
              {data.summary.net >= 0 ? '+' : ''}{formatBRL(data.summary.net)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <p className="text-xs text-gray-500 uppercase tracking-wider">Saldo Final Projetado</p>
            <p className={`text-lg font-semibold font-mono mt-1 ${(data.opening_balance + data.summary.net) >= 0 ? 'text-blue-700' : 'text-red-700'}`}>
              {formatBRL(data.opening_balance + data.summary.net)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <p className="text-xs text-gray-500 uppercase tracking-wider flex items-center gap-1">
              {minBalanceNegative && <AlertTriangle className="h-3 w-3 text-red-500" />}
              Saldo Mínimo
            </p>
            <p className={`text-lg font-semibold font-mono mt-1 ${minBalanceNegative ? 'text-red-600' : 'text-gray-800'}`}>
              {minBalance != null ? formatBRL(minBalance) : '—'}
            </p>
            {data.summary.min_balance.date && (
              <p className="text-xs text-gray-400 mt-0.5">em {data.summary.min_balance.date}</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Chart */}
      <Card>
        <CardContent>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
            Saldo Projetado — {weeks} semanas
          </h3>
          <div style={{ width: '100%', height: 320 }}>
            <ResponsiveContainer>
              <AreaChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorBalance" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                />
                <Tooltip
                  formatter={(v: any, name: any) => [formatBRL(Number(v) || 0), String(name)]}
                  labelFormatter={(label: any, payload: any) => {
                    const d = payload?.[0]?.payload;
                    return d?.week_range ? `Semana ${label} (${d.week_range})` : String(label);
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <ReferenceLine y={0} stroke="#ef4444" strokeDasharray="4 2" />
                {minBalance != null && (
                  <ReferenceLine
                    y={minBalance}
                    stroke="#f59e0b"
                    strokeDasharray="2 2"
                    label={{ value: `Mín ${formatBRL(minBalance)}`, position: 'insideBottomRight', fontSize: 10, fill: '#f59e0b' }}
                  />
                )}
                <Area
                  type="monotone"
                  dataKey="balance"
                  name="Saldo"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  fill="url(#colorBalance)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* By group */}
      <Card>
        <CardContent>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
            Resumo por Grupo (DFC)
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-gray-500">
                  <th className="text-left py-2 px-3 font-medium">Grupo</th>
                  <th className="text-right py-2 px-3 font-medium">Entradas</th>
                  <th className="text-right py-2 px-3 font-medium">Saídas</th>
                  <th className="text-right py-2 px-3 font-medium">Net</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(data.summary.by_group).map(([group, v]) => {
                  const net = v.inflows - v.outflows;
                  return (
                    <tr key={group} className="border-b border-gray-100">
                      <td className="py-2 px-3 text-gray-700 font-medium">
                        {GROUP_LABELS[group] || group}
                      </td>
                      <td className="py-2 px-3 text-right font-mono text-green-600">
                        {v.inflows > 0 ? formatBRL(v.inflows) : '—'}
                      </td>
                      <td className="py-2 px-3 text-right font-mono text-red-600">
                        {v.outflows > 0 ? formatBRL(v.outflows) : '—'}
                      </td>
                      <td className={`py-2 px-3 text-right font-mono font-medium ${net >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                        {net >= 0 ? '+' : ''}{formatBRL(net)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Weekly breakdown */}
      <Card>
        <CardContent>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
              Detalhamento Semanal
            </h3>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <button
                className="hover:text-blue-600"
                onClick={() => setExpanded(new Set(data.weeks.map((_, i) => i)))}
              >
                Expandir todas
              </button>
              <span>·</span>
              <button
                className="hover:text-blue-600"
                onClick={() => setExpanded(new Set())}
              >
                Colapsar todas
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-gray-500 text-xs">
                  <th className="w-8"></th>
                  <th className="text-left py-2 px-3 font-medium">#</th>
                  <th className="text-left py-2 px-3 font-medium">Semana</th>
                  <th className="text-right py-2 px-3 font-medium">Saldo Inicial</th>
                  <th className="text-right py-2 px-3 font-medium">Entradas</th>
                  <th className="text-right py-2 px-3 font-medium">Saídas</th>
                  <th className="text-right py-2 px-3 font-medium">Net</th>
                  <th className="text-right py-2 px-3 font-medium">Saldo Final</th>
                </tr>
              </thead>
              <tbody>
                {data.weeks.map((w, i) => (
                  <WeekRow
                    key={w.week_start}
                    week={w}
                    index={i}
                    expanded={expanded.has(i)}
                    onToggle={() => {
                      const next = new Set(expanded);
                      if (next.has(i)) next.delete(i); else next.add(i);
                      setExpanded(next);
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Footer note */}
      <p className="text-xs text-gray-400 text-center">
        Fontes: realizado no ledger · empréstimos (parcelas projetadas) · obrigações provisionadas ·
        fallback histórico (últimos 12 meses) · gerado em {new Date(data.generated_at).toLocaleString('pt-BR')}
      </p>
    </div>
  );
}
